/* eslint-disable no-param-reassign */
/* eslint-disable react/jsx-curly-newline */
import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { isMobile } from 'react-device-detect';

import BetPanelBase, {
  BetPanelBasicProps,
  BetPanelRefs,
} from '@/components/layouts/Modal/BetPanel';
import Complete from '@/components/layouts/Modal/BetPanel/Partials/Complete/Singlerace';
import ConfirmBox from '@/components/layouts/Modal/BetPanel/Partials/ConfirmBox';
import TicketBox from '@/components/layouts/Modal/BetPanel/Partials/TicketBox/Singlerace';
import DeleteAllBetDataModal from '@/components/layouts/Modal/DeleteAllBetData';
import { BET_PRICE } from '@/config/constants/bet';
import {
  BetType,
  ItemInput,
  OrderSingleRaceInput,
  Race,
  RaceProgram,
} from '@/lib/apollo/types/gen/graphql';
import { getCompositeOdds, getOddsRange, getRefund } from '@/lib/race';
import {
  BetData,
  BetDataList,
  BetDetail,
  CompositeOddsDetail,
  OddsData,
  OrderStep,
  Ticket,
} from '@/modelTypes';
import { raceSelector } from '@/selectors/race';

import SelectCharge from '../SelectCharge';
import { ContentWrapper, LeftWrapper, RightWrapper } from '../style';

interface SingleraceBetPanelProps extends BetPanelBasicProps {
  race: Race;
  raceProgram: RaceProgram;
  racePrograms: RaceProgram[];
  oddsData: OddsData;
  betDataList: BetDataList;
  setBetDataList: Dispatch<SetStateAction<BetDataList>>;
  isProcessing: boolean;
  modifyTheBetDetail: (betDetail: BetDetail, key: string) => void;
  modifyBetDetailKey: string;
  cancelModifyTheBetDetail: () => void;
  orderStep: OrderStep;
  setOrderStep: Dispatch<SetStateAction<OrderStep>>;
  orderRace: (
    input: OrderSingleRaceInput,
    serviceCode: string,
  ) => Promise<void>;
}

interface SingleraceBetPanelContentProps
  extends SingleraceBetPanelProps,
    BetPanelRefs {}

const SingleraceBetPanelContent: FC<SingleraceBetPanelContentProps> = ({
  race,
  raceProgram,
  racePrograms,
  oddsData,
  betDataList,
  setBetDataList,
  isProcessing,
  modifyTheBetDetail,
  modifyBetDetailKey,
  cancelModifyTheBetDetail,
  containerRef,
  scrollBoxRef,
  orderStep,
  setOrderStep,
  orderRace,
}) => {
  const [isDeleteAllBetOpen, setIsDeleteAllBetOpen] = useState<boolean>(false);

  // 注文ステータスの更新の度にスクロールトップに移動
  useEffect(() => {
    if (isMobile) {
      containerRef &&
        containerRef.current &&
        containerRef.current.scrollTo(0, 0);
    } else {
      scrollBoxRef &&
        scrollBoxRef.current &&
        scrollBoxRef.current.scrollTo(0, 0);
    }
  }, [orderStep]);

  const deleteBetData = () => {
    if (modifyBetDetailKey) {
      cancelModifyTheBetDetail();
    }
    setBetDataList(prev => {
      const newData = { ...prev };
      delete newData[race.id];
      return newData;
    });
    setIsDeleteAllBetOpen(false);
  };

  /**
   *  有効な（削除フラグが立っていない）チケットが存在しない場合はdisabled:trueとなる
   * */
  const disabled = useMemo(() => {
    const betData = betDataList[race.id] || {};
    const allValidTicket = Object.keys(betData)
      .map(key => betData[key].tickets)
      .flat()
      .filter(ticket => !ticket.isRemoved);
    return !allValidTicket.length || !raceSelector(race).isHeld;
  }, [betDataList, race.id, raceSelector(race).isHeld]);

  /**
   * 当該レースに関係する購入予定組番情報のみを取得し、OrderStepがconfirmの場合は、
   * さらにそこから削除フラグが立っていないものだけを抽出する
   */
  const filteredBetData = useMemo(() => {
    const originalData = betDataList[race.id] || {};
    if (orderStep === OrderStep.Ready) {
      return originalData;
    }
    const filteredData: BetData = {};
    Object.keys(originalData).forEach(key => {
      filteredData[key] = {
        ...originalData[key],
        tickets: originalData[key].tickets.filter(
          t => !t.isRemoved && t.count > 0,
        ),
      };
    });
    return filteredData;
  }, [betDataList, orderStep]);

  const purchasedCount = useMemo(() => {
    let ticketOfArray: Ticket[] = [];
    Object.values(filteredBetData).forEach(betInfo => {
      ticketOfArray = ticketOfArray.concat(betInfo.tickets);
    });
    return ticketOfArray.filter(t => !t.isRemoved && t.count > 0).length;
  }, [betDataList]);

  const totalPrice = useMemo(() => {
    let count = 0;
    Object.values(filteredBetData).forEach(betInfo => {
      count += betInfo.tickets
        .filter(t => !t.isRemoved)
        .map(t => t.count)
        .reduce((sum, element) => sum + element, 0);
    });
    return count * BET_PRICE;
  }, [betDataList]);

  const compositeOddsDetail = useMemo(() => {
    const result: CompositeOddsDetail = {
      min: 0,
      minRefund: 0,
      max: 0,
      maxRefund: 0,
      composite: 0,
    };
    const originalData = betDataList[race.id] || {};
    Object.values(originalData).forEach(bet => {
      if (bet.tickets.length === 0) {
        return;
      }

      const setNums: number[] = [];
      bet.tickets.forEach(t => {
        if (!t.isRemoved) {
          setNums.push(t.setNum);

          const assumptionRefund = getRefund(
            oddsData.data,
            t.setNum,
            t.count,
            bet.betType,
          );
          if (result.minRefund == 0 || result.minRefund > assumptionRefund) {
            result.minRefund = assumptionRefund;
          }
          if (result.maxRefund == 0 || result.maxRefund < assumptionRefund) {
            result.maxRefund = assumptionRefund;
          }
        }
      });

      if (setNums.length === 0) {
        return;
      }

      const oddsMinMax = getOddsRange(oddsData.data, setNums, bet.betType);
      if (result.min == 0 || result.min > oddsMinMax.min) {
        result.min = oddsMinMax.min;
      }
      if (result.max == 0 || result.max < oddsMinMax.max) {
        result.max = oddsMinMax.max;
      }

      const composite = getCompositeOdds(oddsData.data, setNums, bet.betType);
      result.composite += 1 / composite;
    });

    result.composite = 1 / result.composite;
    if (!Number.isFinite(result.composite)) {
      result.composite = 0; // 有効数でない場合は「0」を設定する
    }
    return result;
  }, [betDataList]);

  const purchaseTickets = (userDevice: string) => {
    if (isProcessing) {
      return;
    }
    // 修正中の買い目がある場合は、修正をキャンセルしそのまま購入処理に遷移する
    if (modifyBetDetailKey) {
      cancelModifyTheBetDetail();
    }

    const items: ItemInput[] = [];
    (Object.keys(filteredBetData) as BetType[]).forEach(key => {
      filteredBetData[key].tickets.forEach(ticket => {
        items.push({
          betType: filteredBetData[key].betType,
          numbers: ticket.setNum
            .toString()
            .split('')
            .map(x => +x),
          count: ticket.count,
        });
      });
    });

    const orderSingleRaceInput: OrderSingleRaceInput = {
      raceId: race.id,
      items,
    };
    orderRace(orderSingleRaceInput, userDevice);
  };

  return (
    <>
      {orderStep === OrderStep.Complete && (
        <Complete racePrograms={racePrograms} betDataList={betDataList} />
      )}
      {orderStep === OrderStep.OtherCharge && (
        <SelectCharge setOrderStep={setOrderStep}></SelectCharge>
      )}
      {(orderStep === OrderStep.Ready || orderStep === OrderStep.Confirm) && (
        <ContentWrapper>
          <LeftWrapper ref={scrollBoxRef}>
            <TicketBox
              race={race}
              velodromeCode={raceProgram.velodromeCode}
              oddsData={oddsData}
              betData={filteredBetData}
              status={orderStep}
              setBetDataList={setBetDataList}
              modifyTheBetDetail={modifyTheBetDetail}
              modifyBetDetailKey={modifyBetDetailKey}
              openDeleteAllBetModal={() => setIsDeleteAllBetOpen(true)}
              compositeOddsDetail={compositeOddsDetail}
            />
            <DeleteAllBetDataModal
              isOpen={isDeleteAllBetOpen}
              deleteFunc={deleteBetData}
              handleClose={() => setIsDeleteAllBetOpen(false)}
            />
          </LeftWrapper>
          <RightWrapper>
            <ConfirmBox
              orderStep={orderStep}
              setOrderStep={setOrderStep}
              totalBetCount={purchasedCount}
              totalPrice={totalPrice}
              purchaseTickets={purchaseTickets}
              disabled={disabled}
              isProcessing={isProcessing}
            />
          </RightWrapper>
        </ContentWrapper>
      )}
    </>
  );
};

const SingleraceBetPanel: FC<SingleraceBetPanelProps> = props => {
  const { isOpen, handleClose, walletQueryRefetch } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollBoxRef = useRef<HTMLDivElement>(null);

  return (
    <BetPanelBase
      isOpen={isOpen}
      handleClose={handleClose}
      containerRef={containerRef}
      walletQueryRefetch={walletQueryRefetch}
    >
      <SingleraceBetPanelContent
        {...props}
        containerRef={containerRef}
        scrollBoxRef={scrollBoxRef}
      />
    </BetPanelBase>
  );
};

export default SingleraceBetPanel;
